import type { InvestorType } from "../../api";

// Typed client for the Advisor Workbench API (/api/advisory, M14). Lives beside
// the feature so web/src/api.ts stays untouched. Same transport rules as the
// shared helper (cookies + the X-Requested-With CSRF header), but a failed call
// throws an AdvisoryApiError that keeps the status and the JSON body — the
// workbench needs QP's 422 requirement list, not just a message.

export class AdvisoryApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown>,
  ) {
    super(message);
    this.name = "AdvisoryApiError";
  }
}

async function http<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "X-Requested-With": "fetch" };
  const init: RequestInit = { method, credentials: "include", headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new AdvisoryApiError("Could not reach the Andersen server.", 0, {});
  }
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    // Non-JSON body (proxy error page). Fall back to the status text.
  }
  if (!res.ok) {
    const msg = typeof data.error === "string" ? data.error : `HTTP ${res.status} ${res.statusText}`.trim();
    throw new AdvisoryApiError(msg, res.status, data);
  }
  return data as T;
}

// ---- Shapes (only what the UI reads; QP content is forwarded as-is) -----------------

export type Json = Record<string, unknown>;
export type I18n = Record<string, string> | null | undefined;

export interface AdvisorySession {
  id: number;
  investor_id: number | null;
  advisor_user_id: number | null;
  qp_client_id: string | null;
  qp_owner_id: string | null;
  flow_id: string | null;
  flow_name: string | null;
  flow_instance_id: string | null;
  status: string;
  percent: number | null;
  advice_json: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  investor_name: string | null;
  investor_type: InvestorType | null;
  advisor_username: string | null;
}

export type StepStatus = "open" | "complete" | "not_applicable" | string;

/** One row of GET /status?workflow=internal → steps[]. */
export interface StatusStep {
  pageId?: string;
  pageName?: string;
  goalId?: string;
  portfolioId?: string;
  kind: string; // "field" | "component" | "snapshot" | "advisor_note" | "instance_snapshot" | …
  componentId?: string;
  componentType?: string;
  snapshotType?: string;
  fieldId?: string;
  fieldName?: string;
  layoutId?: string;
  tabId?: string;
  reason?: string;
  status: StepStatus;
}

export interface FlowStatus {
  status: string;
  canSubmit: boolean;
  percent: number;
  totalRequired?: number;
  filledRequired?: number;
  steps: StatusStep[];
}

export interface ProcessApproval {
  id: string;
  stepType: string;
  status: string;
  comment: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface FlowProcess {
  status: string;
  steps: string[];
  cancelledAt: string | null;
  completedAt: string | null;
  externalStatus: string | null;
  flags: {
    requireComplianceApproval?: boolean;
    requireExternalApproval?: boolean;
    enableEidSigning?: boolean;
    enableDigitalSigning?: boolean;
  } | null;
  approvals?: ProcessApproval[];
}

export interface SessionState {
  session: AdvisorySession;
  status: FlowStatus | null;
  process: FlowProcess | null;
  pipeline?: PipelineState | null;
  refreshError?: string | null;
}

export interface AdvisoryEvent {
  id: number;
  actor: string;
  kind: string;
  detail: Json | null;
  created_at: string;
}

export interface FlowOption {
  id: string;
  name: string;
  isDefault: boolean;
}

export interface OwnerMapping {
  user_id: number;
  username: string;
  qp_user_id: string | null;
  qp_email: string | null;
}

export interface GoalType {
  id: string;
  labelI18n: I18n;
  iconSvg: string | null;
  order: number;
}

export interface Goal {
  id: string;
  name: string;
  description: string | null;
  goalType: string | null;
  riskScore: number | null;
  hasRiskAssessment: boolean;
  timeHorizonId: string | null;
  riskLevelId: string | null;
  portfolios: { id: string; portfolioType: string; scheduleType: string | null }[];
}

export interface Position {
  productId?: string;
  name: string;
  isin: string | null;
  assetClassName?: string | null;
  weight: number;
  amount?: number | null;
}

export interface Composition {
  portfolioType: string;
  modelPortfolioName?: string;
  modelResolutionFailure?: string | null;
  positions: Position[];
}

export interface CustomProduct {
  id: string;
  productId: string;
  amount: number | string | null;
  weight: number | string | null;
  product?: { nameI18n?: I18n; isin?: string | null } | null;
}

export interface Portfolio {
  id: string;
  goalId: string;
  name: string;
  portfolioType: "model" | "custom" | "dynamic" | string;
  scheduleType: "one_time" | "recurring" | string | null;
  oneTimeAmount: string | number | null;
  oneTimeDate: string | null;
  recurringAmount: string | number | null;
  recurringFrequency: string | null;
  recurringStartDate: string | null;
  recurringEndDate: string | null;
  riskBoundDecision?: unknown;
  customProducts?: CustomProduct[];
  minBuyViolations?: unknown[];
}

export interface RiskOptions {
  timeHorizons: { id: string; label: string; order: number }[];
  riskLevels: { id: string; label: string; order: number }[];
  adjustmentQuestions: { id: string; label: string; answers: { id: string; label: string }[] }[];
  cap: { applies: boolean; level: number | null } | null;
}

export interface RiskResult {
  score: number | null;
  scoreFromMatrix?: number | null;
  scoreAdjustment?: number | null;
  isCapped?: boolean;
  timeHorizonName?: string;
  riskLevelName?: string;
  selectedPortfolioId?: string | null;
  modelResolutionFailure?: string | null;
}

export interface RiskCurrent {
  submitted?: boolean;
  timeHorizonId?: string | null;
  riskLevelId?: string | null;
  adjustmentAnswers?: Record<string, string> | null;
  result?: RiskResult | null;
}

export interface Snapshot {
  snapshotType: string;
  scope: string;
  goalId: string | null;
  portfolioId: string | null;
  generatedAt?: string;
  data: Json;
}

export interface ResultsBundle {
  goals: {
    goal: Goal;
    risk: RiskCurrent | null;
    portfolios: { portfolio: Portfolio; composition: Composition | null; snapshots: Snapshot[] }[];
    snapshots: Snapshot[];
  }[];
  instanceSnapshots: Snapshot[];
}

export interface Product {
  id: string;
  nameI18n: I18n;
  isin: string | null;
  productTypeId: string | null;
  minTimeHorizonId: string | null;
  minRiskLevelId: string | null;
  minRiskScore: number | null;
  minBuyAmount: number | null;
  costValues?: { value: number }[];
}

export interface Pdf {
  id: string;
  templateName: string;
  status: string;
  generatedAt: string | null;
  url?: string | null;
}

export interface Prefill {
  stepKey: string;
  source: "andersen" | "quantfolio_previous";
  proposal: Json;
  basis: string[];
  /** Demo estimates mixed into a financial-situation proposal (see the server's estimateFinancials). */
  estimates?: { keys: string[]; rowPrefix: string; basis: string[] };
}

/** After submit: the advice's way to orders — Andersen's gate, then the order inbox. */
export interface PipelineState {
  stage: string;
  gate: {
    checkId: number;
    status: "pending" | "executed" | "canceled";
    decidedBy: string | null;
    decidedAt: string | null;
    openedBy: "workbench" | "webhook" | null;
  } | null;
  inbox: { orders: number; savingsPlans: number; open: number; lastIngestAt: string | null };
}

export interface Applicability {
  knowledge: { applicable: boolean; typesInUse: number; productTypes: string[] };
}

export interface StepLoad<T = Json> {
  stepKey: string;
  scope: string;
  readOnly: boolean;
  data: T;
}

export interface Withdrawal {
  id: string;
  name: string;
  scheduleType: string;
  oneTimeAmount: number | null;
  oneTimeDate: string | null;
  recurringAmount: number | null;
  recurringFrequency: string | null;
  recurringStartDate: string | null;
  recurringEndDate: string | null;
}

export interface HandoffResult {
  embed: { id: number; embed_url: string | null; embeddable: 0 | 1 | null; embed_block: string | null };
  launchUrl: string | null;
  process: FlowProcess | null;
}

export interface ClientContextData {
  investor: {
    id: number;
    name: string;
    type: InvestorType;
    email: string | null;
    phone: string | null;
    city: string | null;
    stage: string;
    risk_profile: number | null;
    kyc_status: string;
    kyc_reviewed_at: string | null;
  };
  portfolio: {
    total_value: number;
    total_return_amount: number;
    total_return_pct: number;
    nok: { total_nok: number } | null;
    accounts: {
      id: number;
      name: string;
      currency: string;
      value: number;
      securities_value: number;
      cash_balance: number;
      return_pct: number;
      positions: { id: number; name: string; value: number }[];
    }[];
  } | null;
  valuationHistory: { date: string; value: number }[];
  recentActivities: { id: number; kind: string; subject: string; occurred_at: string }[];
  openOrders: { id: number; side: string; instrument_name: string | null; status: string }[];
  advisorySessions: { id: number; status: string; percent: number | null; updated_at: string }[];
}

/** Where a step lives: page / goal / portfolio ids as query parameters. */
export interface StepAddr {
  pageId?: string;
  goalId?: string;
  portfolioId?: string;
}

// ---- Client ------------------------------------------------------------------------------

const BASE = "/api/advisory";

function qs(addr: StepAddr | undefined): string {
  if (!addr) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(addr)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

const S = (id: number) => `${BASE}/sessions/${id}`;
const goalPath = (id: number, goalId: string) => `${S(id)}/goals/${encodeURIComponent(goalId)}`;
const portfolioPath = (id: number, goalId: string, pid: string) =>
  `${goalPath(id, goalId)}/portfolios/${encodeURIComponent(pid)}`;

type Written = { result: Json; state: SessionState };

export const advisoryApi = {
  flows: () => http<{ flows: FlowOption[] }>("GET", `${BASE}/flows`),
  owners: () => http<{ mappings: OwnerMapping[] }>("GET", `${BASE}/advisors/qp-users`),
  context: (investorId: number) => http<ClientContextData>("GET", `${BASE}/context/${investorId}`),

  sessions: (filter?: string) =>
    http<{ sessions: AdvisorySession[] }>(
      "GET",
      `${BASE}/sessions${filter ? `?status=${encodeURIComponent(filter)}` : ""}`,
    ),
  create: (body: { investorId: number; flowId?: string; ownerUserId?: number }) =>
    http<{ session: AdvisorySession }>("POST", `${BASE}/sessions`, body),
  session: (id: number) => http<SessionState>("GET", S(id)),
  /** Re-read QP now and drive the gate/inbox pipeline (the after-submit Refresh). */
  sync: (id: number) => http<SessionState>("POST", `${S(id)}/sync`, {}),
  applicability: (id: number) => http<Applicability>("GET", `${S(id)}/applicability`),
  goalTypes: (id: number) => http<{ source: string; data: GoalType[] }>("GET", `${S(id)}/goal-types`),
  events: (id: number) => http<{ events: AdvisoryEvent[] }>("GET", `${S(id)}/events`),

  loadStep: <T = Json>(id: number, key: string, addr?: StepAddr) =>
    http<StepLoad<T>>("GET", `${S(id)}/steps/${key}${qs(addr)}`),
  saveStep: (id: number, key: string, addr: StepAddr | undefined, body: Json) =>
    http<{ stepKey: string; result: Json; state: SessionState }>("PUT", `${S(id)}/steps/${key}${qs(addr)}`, body),
  prefill: (id: number, key: string, addr?: StepAddr) =>
    http<Prefill>("GET", `${S(id)}/prefill/${key}${qs(addr)}`),
  runAml: (id: number, provider: string, body: Json) =>
    http<{ result: Json }>("POST", `${S(id)}/aml/${provider}`, body),

  goals: (id: number) => http<{ data: Goal[] }>("GET", `${S(id)}/goals`),
  patchPortfolio: (id: number, goalId: string, pid: string, body: Json) =>
    http<Written>("PATCH", portfolioPath(id, goalId, pid), body),
  addProduct: (id: number, goalId: string, pid: string, body: { productId: string; amount: number }) =>
    http<Written>("POST", `${portfolioPath(id, goalId, pid)}/products`, body),
  patchProduct: (id: number, goalId: string, pid: string, rowId: string, body: { amount: number }) =>
    http<Written>("PATCH", `${portfolioPath(id, goalId, pid)}/products/${encodeURIComponent(rowId)}`, body),
  deleteProduct: (id: number, goalId: string, pid: string, rowId: string) =>
    http<Written>("DELETE", `${portfolioPath(id, goalId, pid)}/products/${encodeURIComponent(rowId)}`),
  composition: (id: number, goalId: string, pid: string) =>
    http<{ data: Composition }>("GET", `${portfolioPath(id, goalId, pid)}/composition`),
  portfolioSustainability: (id: number, goalId: string, pid: string) =>
    http<{ data: Json }>("GET", `${portfolioPath(id, goalId, pid)}/sustainability`),
  products: (id: number, search?: string) =>
    http<{ collectionId: string; data: Product[] }>(
      "GET",
      `${S(id)}/products?limit=50${search ? `&search=${encodeURIComponent(search)}` : ""}`,
    ),

  wealthPlan: (id: number) => http<{ data: Withdrawal[] }>("GET", `${S(id)}/wealth-plan`),
  addWithdrawal: (id: number, body: Json) => http<Written>("POST", `${S(id)}/wealth-plan`, body),
  deleteWithdrawal: (id: number, wid: string) =>
    http<Written>("DELETE", `${S(id)}/wealth-plan/${encodeURIComponent(wid)}`),

  results: (id: number) => http<ResultsBundle>("GET", `${S(id)}/results`),
  submit: (id: number) => http<Written>("POST", `${S(id)}/submit`, {}),
  pdfs: (id: number) => http<{ data: Pdf[] }>("GET", `${S(id)}/pdfs`),
  generatePdfs: (id: number) => http<{ data: Pdf[]; errors?: unknown[] }>("POST", `${S(id)}/pdfs`, {}),
  pdfUrl: (id: number, pdfId: string) =>
    http<{ url: string }>("GET", `${S(id)}/pdfs/${encodeURIComponent(pdfId)}/url`),
  handoff: (id: number) => http<HandoffResult>("POST", `${S(id)}/signing-handoff`, {}),
};

/** True when the failure is QP (or the network) being unavailable, not a user error. */
export function isUnavailable(e: unknown): boolean {
  return e instanceof AdvisoryApiError && (e.status === 0 || e.status === 502 || e.status === 503 || e.status === 504);
}
